import { Button, Form } from '@ui/themed';
import { t } from 'i18next';
import { useAppForm } from '../../../lib/app-form';
import { useFormSubmitHandlers } from '../hooks/use-form-submit-handlers';
import { useCreateSection, useUpdateSection } from '../hooks/use-sections';
import { type SectionFormValues, sectionFormSchema } from '../section-schemas';
import { formValuesToSectionRequest } from '../section-transformers';
import type { SectionResponse } from '../types/api';
import { ContextFieldsCard } from './context-fields-card';
import { IdentifierFieldsCard } from './identifier-fields-card';
import { SECTION_CONTEXT_FIELDS, SECTION_DETAILS_FIELDS } from './section-fields';

export const sectionFormCreateDefaultValues: SectionFormValues = {
  name: '',
  external_id: '',
  integration_id: '',
  accounting_id: '',
  customer_group: '',
  price_lists: [],
  sales_agent_groups: [],
  payment_on_account_enabled: false,
  payment_on_account: {
    credit_limit: '',
    balance: '',
    currency_code: '',
    net_terms: '',
  },
};

export function SectionForm({
  defaultValues,
  isCreate,
  companyId,
  sectionId,
  existingSection,
  onCancel,
  afterSubmit,
}: {
  defaultValues: SectionFormValues;
  isCreate?: { parent: string | null; sibling: string | null };
  companyId: string;
  sectionId?: string;
  existingSection?: SectionResponse;
  onCancel?: () => void;
  afterSubmit?: () => void;
}) {
  const sectionsPath = `/customers/companies/${companyId}/sections`;
  const submitHandlers = useFormSubmitHandlers(sectionsPath);

  const createSection = useCreateSection(companyId);
  const updateSection = useUpdateSection(companyId, sectionId);

  const form = useAppForm({
    defaultValues,
    validators: { onSubmit: sectionFormSchema },
    onSubmit: async ({ value }) => {
      const payload = formValuesToSectionRequest(
        sectionFormSchema.parse(value),
        isCreate?.parent,
        isCreate?.sibling,
        existingSection,
      );

      if (isCreate) {
        await createSection.mutateAsync(payload, submitHandlers);
      } else if (sectionId) {
        await updateSection.mutateAsync(payload, submitHandlers);
      }
      afterSubmit?.();
    },
  });

  return (
    <Form onSubmit={form.handleSubmit}>
      <IdentifierFieldsCard
        form={form}
        fields={SECTION_DETAILS_FIELDS}
        translationPrefix="features:sections.pages.section_form_page.details"
      />
      <ContextFieldsCard
        form={form}
        fields={SECTION_CONTEXT_FIELDS}
        contextPrefix="features:sections.pages.section_form_page.context"
      />

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button variant="secondary" type="button" onClick={onCancel}>
            {t('buttons:cancel')}
          </Button>
        )}
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <Button
              type="submit"
              disabled={createSection.isPending || updateSection.isPending || isSubmitting}
            >
              {t('buttons:save')}
            </Button>
          )}
        </form.Subscribe>
      </div>
    </Form>
  );
}
